import { Spin } from 'antd';
import { SaveFilled } from '@ant-design/icons';
import { useLayoutEffect, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { Button, Dialog, DialogActions, DialogTitle } from '@mui/material';

import { axiosErrorToString } from '@utils';
import { Crop, CropCategory } from '@types';
import { cropHttp } from '@network/crop-http';
import { CropForm } from '@components/Crops/CropForm';
import cls from './crop-creator.module.css';

const initialFormValues = {
  nameMx: '',
  nameEn: '',
  id: 0,
  categoryId: 0,
  type: null,
  yiel: null,
  averageCost: null,
  averagePrice: null,
  varieties: [],
};

interface CropCategoryCreatorProps {
  type: 'create' | 'edit';
  close: () => void;
  values: Crop | null;
}

export const CropCreator = ({ type, close, values }: CropCategoryCreatorProps) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const prepareValues = (crop: Crop | null) => {
    if (!crop) return initialFormValues;
    
    const varieties = crop.varieties?.map((v: any) => 
      typeof v === 'string' ? v : v.variety,
    ) || [];

    return {
      ...crop,
      varieties,
    };
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState(prepareValues(values));
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<CropCategory | null>(null);

  const closeHandler = () => {
    setFormValues(initialFormValues);
    close();
  };

  const submit = async () => {
    if (formValues.nameMx?.length === 0) {
      setShowErrors(true);

      return;
    }

    const params = {
      nameMx: formValues.nameMx || '',
      nameEn: formValues.nameEn || null,
      categoryId: formValues.categoryId || null,
      type: formValues.type || null,
      yiel: formValues.yiel || null,
      averageCost: formValues.averageCost || null,
      averagePrice: formValues.averagePrice || null,
      varieties: formValues.varieties || [],
    };
    try {
      setIsLoading(true);
      await cropHttp.create(params);

      refresh();
      closeHandler();
    } catch (err) {
      notify(axiosErrorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  const update = async () => {
    if (formValues.nameMx?.length === 0) {
      setShowErrors(true);

      return;
    }

    try {
      setIsLoading(true);
      await cropHttp.update(formValues);

      refresh();
      closeHandler();
    } catch (err) {
      notify(axiosErrorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async () => {
    try {
      setIsLoading(true);
      await cropHttp.remove(formValues.id);

      refresh();
      closeHandler();
    } catch (err) {
      notify(axiosErrorToString(err));
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    document.body.className = 'noscroll';

    return () => {
      document.body.className = '';
    };
  }, []);

  return (
    <div className={cls._}>
      <div className={cls.wrap}>
        <div className={cls.close}>
          <div className={cls.closeIcon} onClick={closeHandler}>
            <svg xmlns="http://www.w3.org/2000/svg"
              style={{ display: 'inline-block', marginTop: -10, marginRight: -40 }}
              width="35"
              height="35"
              viewBox="0 0 35 35"
              fill="none">
              <path d="M17.5039 0C7.83826 0 0 7.83826 0 17.5039C0 27.1695 7.83826 35.0078 17.5039 35.0078C27.1695 35.0078 35.0078 27.1695 35.0078 17.5039C34.9922 7.83826 27.1617 0.00777605 17.5039 0Z"
                fill="var(--primary-color)" />
              <path d="M24.0903 10.9101C23.787 10.6068 23.2971 10.6068 22.9938 10.9101L17.5039 16.4L12.0141 10.9101C11.7108 10.6068 11.2209 10.6068 10.9176 10.9101C10.6144 11.2133 10.6144 11.7032 10.9176 12.0065L16.4075 17.4964L10.9176 22.9863C10.6144 23.2895 10.6144 23.7794 10.9176 24.0827C11.2209 24.386 11.7108 24.386 12.0141 24.0827L17.5039 18.5928L22.9938 24.0827C23.2971 24.386 23.787 24.386 24.0903 24.0827C24.3935 23.7794 24.3935 23.2895 24.0903 22.9863L18.6004 17.4964L24.0903 12.0065C24.3935 11.7032 24.3935 11.2133 24.0903 10.9101Z"
                fill="#FFFFFF" />
            </svg>
          </div>
        </div>

        <div className={cls.title}>
          {type === 'create' && 'Create a new crop'}
          {type === 'edit' && 'Crop'}
        </div>

        <CropForm
          formValues={formValues}
          setFormValues={setFormValues}
          showErrors={showErrors}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />

        {type === 'create' && (
          <div className={cls.actions}>
            <Button className={cls.submit} variant="contained" onClick={submit} disabled={isLoading}>
              {isLoading && <Spin />}
              Add crop
            </Button>
          </div>
        )}

        {type === 'edit' && (
          <div className={cls.actions}>
            <Button className={cls.update} variant="contained" onClick={update} disabled={isLoading}>
              <SaveFilled className={cls.actionIcon} /> Save
            </Button>

            <Button className={cls.delete} variant="contained" onClick={() => setIsDialogOpen(true)} disabled={isLoading}>
              Delete
            </Button>
          </div>
        )}
      </div>

      <div className={cls.overlay} onClick={closeHandler} />

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>
            No
          </Button>
          <Button onClick={remove} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
